// @flow
import React, { useState } from 'react';
import { toastError } from '@presentational/notifications/utils';
import { useUpdateEstimateMutation } from '@src/service-definitions/estimates';
import { useParams } from 'react-router-dom';
import { CheckBoxField } from '@src/formik-utils/FormFields';
import { useEstimateVersion } from '@src/custom-hooks/useEstimateVersion';
import { useIsEstimateRenewal } from '@src/custom-hooks/useIsEstimateRenewal';
import { ConfirmModal } from '@presentational/modals/ConfirmModal';
import { useFormikContext } from 'formik';

import { VBD_ENABLE_FOR_RENEWALS_CONFIRMATION_MODAL_CONFIG } from '../../../components/common/vbd/constants';

const performSGToggleUpdate = async (
  estimateId,
  estimateVersion,
  updateEstimateDetails,
  backendFieldName,
  checked
) => {
  const payload = {
    [backendFieldName]: checked,
    estimate_version: estimateVersion,
  };

  const { error } = await updateEstimateDetails({
    estimateId,
    payload,
  });

  if (error) {
    toastError(error);
  }
};

const handleSGDiscountToggle = async (
  formik,
  estimateId,
  updateEstimateDetails,
  estimateVersion,
  backendFieldName,
  checked,
  isRenewal,
  setModalState
) => {
  // Show confirmation modal only for renewals and when enabling SG discounts (checked === true)
  if (isRenewal && checked === true) {
    // Store what the user is trying to enable
    setModalState({
      isOpen: true,
      pendingChecked: checked,
    });
  } else {
    // For non-renewals or when disabling, proceed without confirmation
    await performSGToggleUpdate(
      estimateId,
      estimateVersion,
      updateEstimateDetails,
      backendFieldName,
      checked
    );
  }
};

export const GDAndMLDSystemGenerationToggleContainer = ({ readOnlyMode, config }) => {
  const { estimateId } = useParams();
  const [updateEstimateDetails] = useUpdateEstimateMutation();
  const estimateVersion = useEstimateVersion();
  const formik = useFormikContext();
  const { backendFieldName, displayName } = config;

  const [modalState, setModalState] = useState({
    isOpen: false,
    pendingChecked: null, // The NEW value user wants to set, null means we don't care
  });

  // Use custom hook to check if estimate is a renewal
  const isRenewal = useIsEstimateRenewal();

  const handleModalConfirm = async () => {
    // User confirmed - proceed with the update
    await performSGToggleUpdate(
      estimateId,
      estimateVersion,
      updateEstimateDetails,
      backendFieldName,
      modalState.pendingChecked
    );
    setModalState({ isOpen: false, pendingChecked: null });
  };

  const handleModalCancel = async () => {
    // todo::CE1 consider using revertFieldsInFrom method instead of hardcoding like this
    // Revert to opposite of what user wanted (they wanted true, so set false)
    const revertValue = !modalState.pendingChecked;
    await formik.setFieldValue(backendFieldName, revertValue);
    setModalState({ isOpen: false, pendingChecked: null });
  };

  const { okText, header, cancelText, body } = VBD_ENABLE_FOR_RENEWALS_CONFIRMATION_MODAL_CONFIG;

  return (
    <>
      <ConfirmModal
        body={body}
        cancelButtonText={cancelText}
        header={header}
        isOpen={modalState.isOpen}
        okButtonText={okText}
        onClickHandlerForCancel={handleModalCancel}
        onClickHandlerForOK={handleModalConfirm}
      />
      <CheckBoxField
        fieldDisplayName={displayName}
        fieldName={backendFieldName}
        onChange={async (event, value, setFieldValue, setFieldTouched, values, checked) => {
          await handleSGDiscountToggle(
            formik,
            estimateId,
            updateEstimateDetails,
            estimateVersion,
            backendFieldName,
            checked,
            isRenewal,
            setModalState
          );
        }}
        readOnlyMode={readOnlyMode}
      />
    </>
  );
};
